
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Typography } from '@mui/material';
import { EllipsisTooltip, PolarisIcon } from '@tmt-insights/oms_fe_core/columns';
import { TechMetadataSection, TechMetadataSpecificationField } from '@/redux/slices/slice_types';
import { isNullOrUndefinedOrEmpty } from '@ibm-aspera/connect-sdk-js/dist/esm/utils';

// Move styled components above Specifications to avoid ReferenceError
const SpecGroup = styled('div')`
  margin-bottom: 2rem;
`;

const SpecHeading = styled(Typography)`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

type SpecificationsProps = {
  specInfo?: TechMetadataSection<TechMetadataSpecificationField>;
};
const Specifications = ({ specInfo }: SpecificationsProps) => {
  const noInfoAvailable = (
    <TitleWrapper>
      <Typography variant="h6" sx={{ fontStyle: 'italic', opacity: 0.5, mt: 2 }}>
        No specifications available.
      </Typography>
    </TitleWrapper>
  );

  if (!specInfo) {
    return noInfoAvailable;
  }

  return (
    <SpecGroup>
      <SpecHeading variant="subtitle1">{specInfo.name}</SpecHeading>
      <Grid container spacing={1}>
        {specInfo.fields.map((field, idx) => (
          <InfoTile key={`${field.field}-${idx}`} label={field.label} highlight={false}>
            {!isNullOrUndefinedOrEmpty(field.expected) ? String(field.expected) : '-'}
          </InfoTile>
        ))}
      </Grid>
    </SpecGroup>
  );
};

export default Specifications;

const InfoTile = ({ label, children, highlight = false }: { label: string; children: React.ReactNode; highlight?: boolean }) => {
  const tileRef = useRef<any>(null);
  const [maxWidth, setMaxWidth] = useState(200);

  useEffect(() => {
    if (tileRef.current) {
      setMaxWidth(tileRef.current.clientWidth - tileRef.current.children[0].clientWidth - 32);
    }
  }, [tileRef.current]);

  // Only pass highlight prop if true, or as string if needed
  const highlightProp = highlight ? { highlight: true } : {};
  return (
    <Grid item xs={6}>
      <TileWrapper ref={tileRef} container {...highlightProp}>
        <LabelWrapper>
          {highlight && <PolarisIcon icon="error_circle" width={20} height={20} />}
          <span>{label}</span>
        </LabelWrapper>
        <span>
          {!tileRef.current ? (
            <PolarisIcon icon="loading_dots" />
          ) : typeof children === 'string' && children?.length > 0 ? (
            <EllipsisTooltip text={children} maxWidth={maxWidth} />
          ) : (
            children || '-'
          )}
        </span>
      </TileWrapper>
    </Grid>
  );
};

const TitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const TileWrapper = styled(Grid, { shouldForwardProp: (prop) => prop !== 'highlight' })<{ highlight?: boolean }>`
  background: ${({ theme }) => theme.palette.background.default};
  min-height: 44px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1, 2)};
  height: 100%;
  align-items: center;

  > div:first-of-type {
    font-size: 1.1rem;
    padding-right: ${({ theme }) => theme.spacing(2)};
  }

  > span {
    font-size: 1.1rem;
    opacity: 0.8;
  }
`;

const LabelWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};

  > span {
    opacity: 0.5;
  }

  svg {
    color: ${({ theme }) => theme.palette.warning.main};
    opacity: 1;
    flex-shrink: 0;
  }
`;